import type { RenderPropArgs } from '@rover/react-lib/src/components/utils/Form';
import type { FromChangesProps } from '@rover/react-lib/src/pages/provider-profile/types/Form';
import type { ValidationErrors } from '@rover/react-lib/src/pages/provider-profile/types/ValidationError';
import type { ServiceAreaPreferences } from '@rover/rsdk/src/apiClient/latest/model/serviceAreaPreferences';
import type { AvailabilityPreferences } from '@rover/types/src/AvailabilityPreferences';
import type { HostingServicePreferences } from '@rover/types/src/HostingServicePreferences';
import type { PetPreferences } from '@rover/types/src/PetPreferences';
import type { ServiceAddOn } from '@rover/types/src/ServiceAddOn';
import type { TrainingServicePreferences } from '@rover/types/src/TrainingServicePreferences';
import type { TravelingServicePreferences } from '@rover/types/src/TravelingServicePreferences';

export type NoneOptions = {
  ownersExpectNone?: boolean | null | undefined;
  dayTimeAvailabilityNone?: boolean | null | undefined;
  hostingOptionsNone?: boolean | null | undefined;
};
export type ServiceFormInitialDataBase = NoneOptions & {
  active: boolean;
  autoUpdateAdditionalRates: boolean;
  availabilityPreferences: AvailabilityPreferences;
  cancellationPolicy: string;
  isAway: boolean;
  isAcceptingNewCustomers: boolean;
  isAcceptingNewRecurringClients: boolean;
  petPreferences: PetPreferences;
  serviceAddons: ServiceAddOn[];
  shouldDisplaySharedPreferencesWarning: boolean;
  dogCareEnabled?: boolean;
  referenceRateSlug?: string;
};
export type TravelingServiceFormData = ServiceFormInitialDataBase & {
  preferences: TravelingServicePreferences | ServiceAreaPreferences;
};
export type HostingServiceFormData = ServiceFormInitialDataBase & {
  preferences: HostingServicePreferences;
};
export type WalkingServiceFormData = ServiceFormInitialDataBase & {
  preferences: TravelingServicePreferences | ServiceAreaPreferences;
};
export type TrainingServiceFormData = ServiceFormInitialDataBase & {
  preferences: TrainingServicePreferences;
};
export type ServiceFormData =
  | TravelingServiceFormData
  | HostingServiceFormData
  | WalkingServiceFormData
  | TrainingServiceFormData;
export type ServiceFormValidationErrors = {
  availabilityPreferences: {
    dayOfTheWeekAvailabilityError: ValidationErrors;
    dayTimeAvailabilityError?: ValidationErrors;
    fullTimeAvailabilityError?: ValidationErrors;
    spacesAvailable: ValidationErrors;
  };
  cancellationPolicy: string | null | undefined;
  petPreferences: {
    dogSizeError: string | null | undefined;
    puppies: string | null | undefined;
  };
  serviceAddons: {
    priceValidatorErrors: ValidationErrors;
    durationValidatorErrors: ValidationErrors;
    recommendedRatesValidatorErrors: ValidationErrors;
  };
  preferences: {
    timeAlone: string | null | undefined;
    homeTypeError: string | null | undefined;
    hostingOptionsTypeError: string | null | undefined;
    ownersExpectTypeError: string | null | undefined;
    radiusInMiles: string | null | undefined;
    yardTypeError: string | null | undefined;
  };
};
// @ts-expect-error - https://github.com/facebook/flow/issues/6040
export type TravelingForm = RenderPropArgs<TravelingServiceFormData>;
// @ts-expect-error - https://github.com/facebook/flow/issues/6040
export type HostingForm = RenderPropArgs<HostingServiceFormData>;
// @ts-expect-error - https://github.com/facebook/flow/issues/6040
export type WalkingForm = RenderPropArgs<WalkingServiceFormData>;
// @ts-expect-error - https://github.com/facebook/flow/issues/6040
export type TrainingForm = RenderPropArgs<TrainingServiceFormData, any>;

/**
 * In here to allow both Hosting and TravelingServiceForm to access the same base type
 */
export type ServiceFormPropsBase = NoneOptions &
  FromChangesProps & {
    onSubmit: (d: ServiceFormData) => void;
    toggleIsDisplayingAdditionalAddOns: (v: boolean) => void;
    showAdditionalRatesModal: boolean;
    setShowAdditionalRatesModal: (v: boolean) => void;
  };

export enum ReturnQueryParam {
  CatExperienceAdded = 'catExperienceAdded',
  ExperienceAdded = 'experienceAdded',
}
